import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { useLanguage } from "@/i18n";
import { HERO_IMAGE } from "@/tours";
import { scrollToId } from "@/components/Navbar";

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  const lines = [t.hero.line1, t.hero.line2];

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE}
          alt="Il porto di Napoli con il Vesuvio sullo sfondo"
          data-testid="hero-image"
          style={{ objectPosition: "center 42%" }}
          className="w-full h-[115%] object-cover animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/25 to-ink/10" />
      </motion.div>

      <motion.div style={{ y: textY }} className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 pb-16 lg:pb-24 pt-40">
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#F3EFE9]/85 mb-6 max-w-xl"
        >
          {t.hero.overline}
        </motion.p>

        <h1 data-testid="hero-title" className="font-serif font-light tracking-tighter leading-[1.02] text-[#FDFBF7] text-5xl sm:text-7xl lg:text-8xl">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.65 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? (
                  <>
                    <span className="italic text-dune">{line.split(" ")[0]}</span> {line.split(" ").slice(1).join(" ")}
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-[#F3EFE9]/90"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-tours"
            onClick={() => scrollToId("#tours")}
            className="rounded-full bg-[#C9A227] px-8 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-[#0F2C50] hover:bg-[#FDFBF7] transition-colors duration-300"
          >
            {t.hero.cta1}
          </button>
          <button
            data-testid="hero-cta-enquiry"
            onClick={() => scrollToId("#richiedi")}
            className="rounded-full border border-[#FDFBF7]/50 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-ink transition-colors duration-300"
          >
            {t.hero.cta2}
          </button>
          <span className="hidden lg:inline-flex items-center gap-2 text-[#F3EFE9]/70 text-xs font-semibold tracking-widest uppercase ml-4">
            <MapPin className="w-3.5 h-3.5" /> {t.hero.badge}
          </span>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#tours")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-6 lg:right-10 text-[#FDFBF7]/70 hover:text-[#FDFBF7] transition-colors duration-300"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ArrowDown className="w-5 h-5" />
        </motion.span>
      </motion.button>
    </section>
  );
}
